import React, { useState, useEffect, useRef } from 'react'
import { PanelBarGLPure, GridSelectionHLPure, ButtonTrashHLPure, InteractiveDialog, ButtonAddHLPure, DialogSystemGLPure, TextAreaHLPure } from '../../pures'
import { ArrowBtnMobile } from '../../../assets/logos/icons'
import { Grid, useMediaQuery } from '@material-ui/core'
import { Tooltip } from '@progress/kendo-react-tooltip'
import { useTranslation } from "react-i18next"
import { Grid as KendoGrid, GridColumn as KendoGridColumn, GridNoRecords } from '@progress/kendo-react-grid'
import RequestDetailsDialog from './RequestDetailsDialog'

/**
 * Componente para mostrar el panel de documentos solicitados en la Bóveda Digital
 * @function DocumentPanelBarDocumentsRequested
 * @param {{
 *   isInDeleteFlow: boolean,
 *   handleDeleteDoc: Function,
 *   filesExpediente: Array,
 *   handleSelectFileExp: Function,
 *   handleUploadDoc: Function,
 *   userIsApplicant: boolean,
 *   userIsValidator: boolean,
 *   userIsShared: boolean,
 *   currentUserId: number,
 *   statusCurrent: number,
 *   infoType: string
 * }} props - Propiedades del componente
 * @param {boolean} props.isInDeleteFlow - Indica si está en flujo de eliminación
 * @param {Function} props.handleDeleteDoc - Función para eliminar documento
 * @param {Array} props.filesExpediente - Array de archivos del expediente desde el servicio
 * @param {Function} props.handleSelectFileExp - Función para seleccionar archivo y renderizar PDF
 * @param {Function} props.handleUploadDoc - Función para cargar documentos
 * @param {boolean} props.userIsApplicant - Si el usuario actual es el solicitante
 * @param {boolean} props.userIsValidator - Si el usuario actual es el validador
 * @param {boolean} props.userIsShared - Si el usuario actual tiene documentos compartidos
 * @param {number} props.currentUserId - ID del usuario actual para identificar documentos propios
 * @param {number} props.statusCurrent - Estado actual de la solicitud (320730 = completado, 320799 = cancelada)
 * @param {string} props.infoType - Tipo de información: 'Expediente' o 'Documento'
 * @returns {JSX.Element} Panel de documentos solicitados
 */
const DocumentPanelBarDocumentsRequested = (props) => {
    let {
        isInDeleteFlow,
        handleDeleteDoc,
        filesExpediente = [],
        handleSelectFileExp,
        handleUploadDoc,
        handleExternalDocumentAction,
        handleCancelRequest,
        handleCloseRequest,
        infoType,
        instructions,
        userIsApplicant = false,
        userIsValidator = false,
        userIsShared = false,
        limitDate,
        currentUserId,
        statusCurrent,
        showSendButton = false
    } = props

    const { t } = useTranslation()

    const isCancelledRequest = statusCurrent === Number(process.env.REACT_APP_ESTATUS_WORKFLOW_LOAD_FILES_GUEST_CANCELED)
    const [showRejectionModal, setShowRejectionModal] = useState(false)
    const [comments, setComments] = useState('')
    const [documentToReject, setDocumentToReject] = useState(null)
    const [isViewingRejection, setIsViewingRejection] = useState(false)
    const [showLimitModal, setShowLimitModal] = useState(false)
    const [expandedUserId, setExpandedUserId] = useState(null)
    const [showDetailsModal, setShowDetailsModal] = useState(false)
    const [selectedRequestForDetails, setSelectedRequestForDetails] = useState(null)
    const [showCancelRequestModal, setShowCancelRequestModal] = useState(false)
    const [showCloseRequestModal, setShowCloseRequestModal] = useState(false)
    const [showMarkNotRequiredModal, setShowMarkNotRequiredModal] = useState(false)
    const [documentToMarkNotRequired, setDocumentToMarkNotRequired] = useState(null)
    const [showApproveRequestModal, setShowApproveRequestModal] = useState(false)
    const [showRejectRequestModal, setShowRejectRequestModal] = useState(false)
    const [showRejectRequestReasonModal, setShowRejectRequestReasonModal] = useState(false)
    const [rejectRequestComments, setRejectRequestComments] = useState('')
    const [showNoFilesWarning, setShowNoFilesWarning] = useState(false)
    const [showCanceledModal, setShowCanceledModal] = useState(false)

    const is530 = useMediaQuery('(max-width:530px)')
    const is1040 = useMediaQuery('(max-width:1040px)')

    const hasAutoClosedRef = useRef(false)

    const isExpedienteWithFreeLoad = infoType === 'Expediente' && filesExpediente.length > 0 && filesExpediente.some(file => file.isFreeLoad === true)

    // Mostrar botón agregar cuando es Expediente (con carga libre O cuando es solicitante viendo documentos de usuarios O cuando es validador)
    const shouldShowAddButton = infoType === 'Expediente' && (
        isExpedienteWithFreeLoad || userIsApplicant || userIsValidator
    )

    /**
     * @function handleAddDocument
     * @description Maneja la carga de documentos para expediente con carga libre o cuando el solicitante agrega documentos
     */
    const handleAddDocument = () => {
        // Si es userIsApplicant, permitir agregar documentos sin restricción de isFreeLoad
        if (userIsApplicant || userIsValidator) {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.mp3,.mp4'
            input.multiple = true

            input.onchange = async (e) => {
                const files = Array.from(e.target.files)

                // Usar el primer usuario disponible o crear estructura base
                const firstUser = filesExpediente[0] || {}

                const freeLoadDataItem = {
                    recipientId: firstUser.recipientId,
                    userEmail: firstUser.userEmail,
                    templateId: null,
                    requestItemId: null,
                    isFreeLoad: true
                }

                for (const file of files) {
                    // Pasar isApplicant como true cuando userIsApplicant es true
                    await handleUploadDoc(freeLoadDataItem, file, true)
                }
            }

            input.click()
            return
        }

        // Lógica original para carga libre
        const freeLoadItem = filesExpediente.find(item => item.isFreeLoad === true)

        if (!freeLoadItem) {
            console.error('No se encontró elemento con isFreeLoad')
            return
        }

        // Crear input file oculto para selección múltiple
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.mp3,.mp4'
        input.multiple = true

        input.onchange = async (e) => {
            const files = Array.from(e.target.files)

            const freeLoadDataItem = {
                ...freeLoadItem,
                templateId: null,
                requestItemId: null,
                isFreeLoad: true
            }

            for (const file of files) {
                // Pasar isApplicant como false cuando NO es userIsApplicant
                await handleUploadDoc(freeLoadDataItem, file, false)
            }
        }

        input.click()
    }

    /**
     * @function getStatusConfig
     * @description Retorna la configuración de botones según el estatus del documento
     * @param {string} status - Pendiente, Cargado, Aceptado, Rechazado
     * @returns {{showCargar: boolean, showEliminar: boolean, showVer: boolean}}
     */
    const getStatusConfig = (status) => {
        switch (status) {
            case 'Pendiente':
                return { showCargar: true, showEliminar: false, showVer: false }
            case 'Cargado':
                return { showCargar: true, showEliminar: true, showVer: false }
            case 'Aceptado':
                return { showCargar: false, showEliminar: false, showVer: true }
            case 'Rechazado':
                return { showCargar: true, showEliminar: false, showVer: false }
            default:
                return { showCargar: false, showEliminar: false, showVer: false }
        }
    }

    /**
     * @function handleExpandChange
     * @description Maneja la expansión/contracción de filas en el grid
     * @param {Object} event - Evento de Kendo Grid
     * @returns {void}
     */
    const handleExpandChange = (event) => {
        const userId = event.dataItem.recipientId
        setExpandedUserId(prevId => prevId === userId ? null : userId)
    }
    /**
     * @function handleShowRequestDetails
     * @description Abre el modal para ver los detalles de la solicitud
     * @param {Object} requestData - Datos de la solicitud completa
     * @returns {void}
     */
    const handleShowRequestDetails = (requestData) => {
        setSelectedRequestForDetails(requestData)
        setShowDetailsModal(true)
    }

    /**
     * @function handleCancelRequest
     * @description Maneja la cancelación de la solicitud
     * @returns {void}
     */
    const handleCancelRequestLocal = () => {
        if (handleCancelRequest) {
            handleCancelRequest()
        }
        setShowCancelRequestModal(false)
    }

    /**
     * @function handleCloseRequest
     * @description Maneja el cierre de la solicitud
     * @returns {void}
     */
    const handleCloseRequestLocal = () => {
        if (handleCloseRequest) {
            handleCloseRequest()
        }
        setShowCloseRequestModal(false)
    }

    /**
     * @function handleCloseRequestClick
     * @description Valida que haya archivos antes de mostrar el modal de confirmación de cierre
     * @returns {void}
     */
    const handleCloseRequestClick = () => {
        const hasUploadedFiles = filesExpediente.some(user =>
            user.documents &&
            user.documents.length > 0 &&
            user.documents.some(doc => doc?.url && doc.url.trim() !== '')
        )

        if (!hasUploadedFiles) {
            setShowNoFilesWarning(true)
        } else {
            setShowCloseRequestModal(true)
        }
    }

    const allUsersCompleted =
        filesExpediente.length > 0 &&
        filesExpediente.every(user =>
            user.documents &&
            user.documents.length > 0 &&
            user.documents.every(doc =>
                doc.status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_ACCEPTED ||
                doc.status === 'EN_BOVEDA' ||
                doc.required === false ||
                doc.status === Number(process.env.REACT_APP_ESTATUS_WORKFLOW_LOAD_FILES_GUEST_COMPLETED)
            )
        )
    const isCompletedWorkflow = statusCurrent === Number(process.env.REACT_APP_ESTATUS_WORKFLOW_LOAD_FILES_GUEST_COMPLETED)
    /**
     * @function useEffect - Cierre automático cuando todos los documentos están completos
     * @description Detecta cuando allUsersCompleted cambia a true y cierra automáticamente la solicitud
     * Punto 1: Todos los documentos aprobados (automático) - status pasa a 320730
     */
    useEffect(() => {
        // Solo ejecutar si:
        // 1. Todos los usuarios completaron
        // 2. Es solicitante (solo el solicitante puede cerrar)
        // 3. No se ha cancelado la solicitud
        // 4. Aún no se ha ejecutado el cierre automático
        // 5. handleCloseRequest está disponible
        if (
            allUsersCompleted &&
            userIsApplicant &&
            !isCancelledRequest &&
            !hasAutoClosedRef.current &&
            handleCloseRequest &&
            statusCurrent !== Number(process.env.REACT_APP_ESTATUS_WORKFLOW_LOAD_FILES_GUEST_COMPLETED)
        ) {
            hasAutoClosedRef.current = true
            handleCloseRequest('close', '')
        }
    }, [allUsersCompleted, userIsApplicant, isCancelledRequest, handleCloseRequest, statusCurrent])

    /**
     * @function handleSubmitRejection
     * @description Maneja el envío del rechazo con el motivo especificado
     * @returns {void}
     */
    const handleSubmitRejection = () => {
        if (!comments.trim()) {
            return
        }

        if (documentToReject && handleExternalDocumentAction) {
            handleExternalDocumentAction(
                documentToReject,
                process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED,
                false,
                comments
            )
        }

        // Limpiar y cerrar modal
        setShowRejectionModal(false)
        setComments('')
        setDocumentToReject(null)
        setIsViewingRejection(false)
    }

    /**
     * @function handleMarkNotRequired
     * @description Maneja la confirmación de marcar documento como no requerido
     * @returns {void}
     */
    const handleMarkNotRequired = () => {
        if (documentToMarkNotRequired && handleExternalDocumentAction) {
            handleExternalDocumentAction(documentToMarkNotRequired, process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REQUIRED, false)
        }

        // Limpiar y cerrar modal
        setShowMarkNotRequiredModal(false)
        setDocumentToMarkNotRequired(null)
    }

    /**
     * @function handleApproveRequest
     * @description Maneja la aprobación de la solicitud por el validador (action: next)
     * @returns {void}
     */
    const handleApproveRequest = () => {

        if (handleCloseRequest) {
            handleCloseRequest('next')
        }

        setShowApproveRequestModal(false)
    }

    /**
     * @function handleRejectRequest
     * @description Maneja el rechazo de la solicitud por el validador (action: back)
     * @returns {void}
     */
    const handleRejectRequest = () => {
        // Primero cerrar el modal de confirmación
        setShowRejectRequestModal(false)

        // Luego abrir el modal para capturar motivo
        setShowRejectRequestReasonModal(true)
    }

    const handleSubmitRejectRequest = () => {
        if (!rejectRequestComments.trim()) return

        if (handleCloseRequest) {
            handleCloseRequest('back', rejectRequestComments)
        }

        setShowRejectRequestReasonModal(false)
        setRejectRequestComments('')
    }


    /**
     * @function UserDocumentsDetailRow
     * @description Componente que renderiza el detalle expandible de documentos de un usuario
     * @param {Object} rowProps - Props de la fila del grid
     * @returns {JSX.Element|null}
     */
    const UserDocumentsDetailRow = (rowProps) => {
        const userDocuments = rowProps?.dataItem?.documents || []

        if (userDocuments.length === 0) return null

        const documentsWithIndex = userDocuments.map((doc, index) => ({
            ...doc,
            _index: index,
            status: doc.status || 'PENDING'
        }))

        const getStatusTranslation = (status) => {
            const statusMap = {
                'PENDING': 'Pendiente',
                [process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_UPLOADED]: 'Cargado',
                [process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_ACCEPTED]: 'Aceptado',
                [process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED]: 'Rechazado'
            }
            return statusMap[status] || status
        }
        const isCompletedWorkflow = statusCurrent === Number(process.env.REACT_APP_ESTATUS_WORKFLOW_LOAD_FILES_GUEST_COMPLETED)

        const handleDocumentAction = (doc, action) => {
            // Crear el objeto con la estructura esperada
            // El 'id' del documento es el 'requestItemId' que espera el backend
            const dataItem = {
                ...doc,
                recipientId: rowProps.dataItem.recipientId,
                requestItemId: doc.id, // Mapear el id del documento como requestItemId
                userEmail: rowProps.dataItem.userEmail,
                document: doc
            }

            if (action === 'view') {
                handleSelectFileExp(dataItem)
            } else if (action === 'upload') {
                handleUploadDoc(dataItem)
            } else if (action === 'delete') {
                handleDeleteDoc(dataItem)
            } else if (action === 'accept') {
                // Aceptar documento - action: ACCEPTED
                if (handleExternalDocumentAction) {
                    handleExternalDocumentAction(dataItem, process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_ACCEPTED, false)
                }
            } else if (action === 'reject') {
                // Rechazar documento - abrir modal para motivo
                setDocumentToReject(dataItem)
                setComments('')
                setIsViewingRejection(false)
                setShowRejectionModal(true)
            } else if (action === 'viewRejection') {
                setDocumentToReject(dataItem)
                setComments(doc.comments || '')
                setIsViewingRejection(true)
                setShowRejectionModal(true)
            } else if (action === 'markNotRequired') {
                // Marcar como no requerido - abrir modal de confirmación
                setDocumentToMarkNotRequired(dataItem)
                setShowMarkNotRequiredModal(true)
            }
        }

        return (
            <KendoGrid
                data={documentsWithIndex}
                width={'105%'}
                resizable
            >
                <KendoGridColumn
                    width={'55%'}
                    headerCell={() => null}
                    cell={(p) => {
                        const status = p.dataItem.status
                        const updatedDate = p.dataItem.updatedDate
                            ? new Date(p.dataItem.updatedDate).toLocaleDateString('es-MX', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                            })
                            : ''

                        let statusText = ''

                        const isAccepted = status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_ACCEPTED || status === 'EN_BOVEDA'

                        if (isAccepted && updatedDate) {
                            statusText = t('externalDocuments.status.accepted', { date: updatedDate }) // "Accepted on {{date}}"
                        } else if (status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_UPLOADED && updatedDate) {
                            statusText = t('externalDocuments.status.uploaded', { date: updatedDate }) // "Uploaded on {{date}}"
                        } else if (status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED && updatedDate) {
                            statusText = t('externalDocuments.status.rejected', { date: updatedDate }) // "Rejected on {{date}}"
                        } else if (status === 'PENDING') {
                            statusText = t('externalDocuments.status.pending') // "Pending"
                        }


                        return (
                            <td style={{ padding: '12px 16px', border: 'none', backgroundColor: 'transparent' }}>
                                <div>
                                    <div
                                        className='document-panel-bar-documentRequested-name'
                                        onClick={() => {
                                            if (p.dataItem.status !== 'PENDING' && p.dataItem.status !== process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED) {
                                                handleDocumentAction(p.dataItem, 'view')
                                            }
                                        }}
                                        style={{
                                            cursor: (p.dataItem.status !== 'PENDING' && p.dataItem.status !== process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED) ? 'pointer' : 'default',
                                            color: (p.dataItem.status !== 'PENDING' && p.dataItem.status !== process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED) ? 'pointer' : 'inherit'
                                        }}
                                    >
                                        {p.dataItem.documentTemplateName || 'Sin nombre'}
                                        {p.dataItem.required && !isAccepted && ' *'}
                                    </div>
                                    {statusText && (
                                        <div className='document-panel-bar-documentRequested-email'>
                                            {statusText}
                                        </div>
                                    )}
                                </div>
                            </td>
                        )
                    }}
                />
                {!is530 && (
                    <KendoGridColumn
                        width={'40%'}
                        headerCell={() => null}
                        cell={(p) => {
                            const status = p.dataItem.status

                            // Calcular porcentaje de tiempo transcurrido
                            let percentageElapsed = 0
                            let isCloseToDeadline = false
                            if (limitDate && p.dataItem.createdDate) {
                                const now = new Date()
                                const limit = new Date(limitDate)
                                const start = new Date(p.dataItem.createdDate)
                                const totalTime = limit - start
                                const elapsedTime = now - start
                                percentageElapsed = (elapsedTime / totalTime) * 100
                                isCloseToDeadline = percentageElapsed >= 70
                            }

                            let trafficLightColor = ''
                            let tooltipText = ''

                            if (status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_ACCEPTED || status === 'EN_BOVEDA') {
                                trafficLightColor = 'rgba(130, 214, 95, 1)'
                                tooltipText = t('documentPanelBarDocumentRequested.tooltip.documentAccepted')
                            } else if ((status === 'PENDING' || status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED) && isCloseToDeadline) {
                                trafficLightColor = 'rgba(255, 143, 0, 1)'
                                if (status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED) {
                                    tooltipText = t('documentPanelBarDocumentRequested.tooltip.documentRejectedNearDeadline')
                                } else {
                                    tooltipText = t('documentPanelBarDocumentRequested.tooltip.documentPendingNearDeadline')
                                }
                            } else if (status === 'PENDING' || status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_UPLOADED || status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED) {
                                trafficLightColor = 'rgba(255, 207, 61, 1)'
                                if (status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED) {
                                    tooltipText = t('documentPanelBarDocumentRequested.tooltip.documentRejected')
                                } else if (status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_UPLOADED) {
                                    tooltipText = t('documentPanelBarDocumentRequested.tooltip.documentUploaded')
                                } else {
                                    tooltipText = t('documentPanelBarDocumentRequested.tooltip.documentPending')
                                }
                            }

                            return (

                                <td className='documentPanelBarDocumentRequested-cell-sub-three-userIsApplicant' style={{ padding: '12px 16px', border: 'none', backgroundColor: 'transparent' }}>
                                    {!showSendButton && (

                                        <Tooltip
                                            openDelay={600}
                                            position='top'
                                            anchorElement='target'
                                            tooltipClassName='tooltipGeneralStyle'
                                        >
                                            <div
                                                className='digitalVaultOperationalReportView-status'
                                                style={{
                                                    backgroundColor: trafficLightColor,
                                                    width: '24px',
                                                    height: '24px',
                                                    borderRadius: '50%',
                                                    cursor: 'pointer'
                                                }}
                                                title={tooltipText}
                                            />
                                        </Tooltip>
                                    )}
                                </td>
                            )
                        }}
                    />
                )}
                <KendoGridColumn
                    width={'40%'}
                    headerCell={() => null}
                    cell={(p) => {
                        const status = getStatusTranslation(p.dataItem.status)

                        const shouldShowViewButton = (
                            !isCompletedWorkflow &&
                            (
                                p.dataItem.status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_ACCEPTED ||
                                p.dataItem.status === 'EN_BOVEDA' ||
                                p.dataItem.status === Number(process.env.REACT_APP_ESTATUS_WORKFLOW_LOAD_FILES_GUEST_COMPLETED)
                            )
                        )

                        const hideViewButtonDueToCompletion = !isCompletedWorkflow && allUsersCompleted

                        return (
                            <td className='documentPanelBarDocuments-scroll'>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    {!isCancelledRequest && !isCompletedWorkflow && !allUsersCompleted && status === 'Pendiente' && (
                                        <button className='grid-btn-action' onClick={() => handleDocumentAction(p.dataItem, 'markNotRequired')}>
                                            <p>{t('documentPanelBarDocumentsRequested.modal.markNotRequired')}</p>
                                        </button>
                                    )}
                                    {!isCancelledRequest && status === 'Cargado' && (
                                        <>
                                            <button className='grid-btn-action' onClick={() => handleDocumentAction(p.dataItem, 'accept')}>
                                                <p>{t('button.accept')}</p>
                                            </button>
                                            <button className='grid-btn-action' onClick={() => handleDocumentAction(p.dataItem, 'reject')}>
                                                <p>{t('button.reject')}</p>
                                            </button>
                                        </>
                                    )}
                                    {shouldShowViewButton && !hideViewButtonDueToCompletion && (
                                        <>
                                            <button
                                                className='grid-btn-action'
                                                onClick={() => handleDocumentAction(p.dataItem, 'view')}
                                                style={{ padding: '6px 12px' }}
                                            >
                                                <p>{t('documentPanelBarExpediente-watch-doc')}</p>
                                            </button>
                                            {!isCancelledRequest &&
                                                !isCompletedWorkflow &&
                                                !(currentUserId && rowProps.dataItem.userId === currentUserId) && (
                                                    <button
                                                        onClick={() => handleDocumentAction(p.dataItem, 'delete')}
                                                        style={{
                                                            background: 'transparent',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            color: '#E74C3C',
                                                            fontSize: '18px',
                                                            padding: '0'
                                                        }}
                                                        title={t('button.delete')}
                                                    >
                                                        <i className='k-icon k-i-delete k-i-trash' />
                                                    </button>
                                                )}
                                        </>
                                    )}
                                </div>
                            </td>
                        )
                    }}
                />
                <GridNoRecords>
                    <span style={{
                        color: "#2C353C",
                        fontSize: "14px",
                        fontWeight: 550,
                        fontFamily: "Quicksand"
                    }}>
                        {t("configurationDomainPanel.notResults")}
                    </span>
                </GridNoRecords>
            </KendoGrid>
        )
    }

    // Headers y cells para el grid cuando userIsApplicant = true
    const headerColumnUserName = (props) => {
        return <span className='documentPanelBarDocumentRequested-header-cell-one'>{props.title}</span>
    }

    const headerColumnUserEmail = (props) => {
        return <span className='documentPanelBarDocumentRequested-header-cell-two'>{props.title}</span>
    }

    const headerColumnUserStatus = (props) => {
        return <span className='documentPanelBarDocumentRequested-header-cell-three'>{props.title}</span>
    }

    const cellColumnUserName = (props) => {
        const { dataItem } = props
        const userName = dataItem.userEmail || 'Sin nombre'
        const userFullName = dataItem.fullName

        return (
            <td className={`documentPanelBarDocumentRequested-cell-one ${dataItem.selected ? 'documentPanelBarVersions-cell-selected' : ''}`}>
                <div className='gridHLPure-userName'>
                    <div className='document-panel-bar-documentRequested-name'>
                        {userFullName}
                    </div>
                    <div className='document-panel-bar-documentRequested-email'>{userName}</div>
                </div>
            </td>
        )
    }

    const cellColumnUserEmail = (props) => {
        const { dataItem } = props

        const isSelfUser = currentUserId && dataItem.userId === currentUserId

        return (
            <td className={`documentPanelBarDocumentRequested-cell-two-userIsApplicant  ${dataItem.selected ? 'documentPanelBarVersions-cell-selected' : ''}`}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {!isSelfUser && !showSendButton && !allUsersCompleted && (
                        <button className='grid-btn-action' onClick={() => {
                            const requestData = {
                                documentName: dataItem.documentName,
                                instructions: dataItem.instructions,
                                isFreeLoad: dataItem.isFreeLoad,
                                userEmail: dataItem.userEmail,
                                infoType: infoType,
                                limitDate: limitDate,
                                usersDocuments: [dataItem]
                            }
                            handleShowRequestDetails(requestData)
                        }}>
                            <p>{t('documentPanelBarDocumentRequested.verDetalle')}</p>
                        </button>
                    )}
                </div>
            </td>
        )
    }

    const cellColumnUserStatus = (props) => {
        const { dataItem } = props
        const documents = dataItem.documents || []

        const isFreeLoadAndEmpty = dataItem.isFreeLoad && (!documents || documents.length === 0)
        let allAcceptedOrNotRequired = false

        if (!isFreeLoadAndEmpty) {
            allAcceptedOrNotRequired = documents.every(doc =>
                doc.status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_ACCEPTED ||
                doc.status === 'EN_BOVEDA' ||
                doc.required === false
            )
        }
        const hasPendingOrRejected = documents.some(doc =>
            doc.status === 'PENDING' || doc.status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_REJECTED || doc.status === process.env.REACT_APP_EXTERNAL_DOCUMENTS_NOTIFICATION_TYPE_UPLOADED
        )

        let percentageElapsed = 0
        let isCloseToDeadline = false
        if (limitDate) {
            const now = new Date()
            const limit = new Date(limitDate)
            const start = new Date(documents[0]?.createdDate || now)
            const totalTime = limit - start
            const elapsedTime = now - start
            percentageElapsed = (elapsedTime / totalTime) * 100
            isCloseToDeadline = percentageElapsed >= 70
        }

        let trafficLightColor = ''
        let tooltipText = ''


        if (!documents || documents.length === 0) {
            trafficLightColor = 'rgba(255, 207, 61, 1)'
            tooltipText = t('documentPanelBarDocumentRequested.tooltip.requestPending')
        } else if (allAcceptedOrNotRequired) {
            trafficLightColor = 'rgba(130, 214, 95, 1)'
            tooltipText = t('documentPanelBarDocumentRequested.tooltip.requestCompleted')
        } else if (hasPendingOrRejected && isCloseToDeadline) {
            trafficLightColor = 'rgba(255, 143, 0, 1)'
            tooltipText = t('documentPanelBarDocumentRequested.tooltip.requestNearDeadline')
        } else if (hasPendingOrRejected) {
            trafficLightColor = 'rgba(255, 207, 61, 1)'
            tooltipText = t('documentPanelBarDocumentRequested.tooltip.requestPending')
        }

        return (
            <td className={`documentPanelBarDocumentRequested-cell-three-userIsApplicant ${dataItem.selected ? 'documentPanelBarVersions-cell-selected' : ''}`}>
                {!showSendButton && (

                    <Tooltip
                        openDelay={600}
                        position='top'
                        anchorElement='target'
                        tooltipClassName='tooltipGeneralStyle'
                    >
                        <div
                            className='digitalVaultOperationalReportView-status'
                            style={{
                                backgroundColor: trafficLightColor,
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                cursor: 'pointer'
                            }}
                            title={tooltipText}
                        />
                    </Tooltip>
                )}
            </td>
        )
    }

    const headerColumnOne = (props) => {
        return <span className='documentPanelBarDocumentRequested-header-cell-one'>{props.title}</span>
    }

    const headerColumnTwo = (props) => {
        return <span className='documentPanelBarDocumentRequested-header-cell-two'>{props.title}</span>
    }

    const headerColumnThree = (props) => {
        return <span className='documentPanelBarDocumentRequested-header-cell-three'>{props.title}</span>
    }

    const cellColumnOne = (props) => {
        const { dataItem } = props
        const isFreeLoad = dataItem.isFreeLoad === true
        const isRequiredDocument = !isFreeLoad && (dataItem.name !== null && dataItem.name !== undefined)
        const hasDocumentUploaded = dataItem.document && dataItem.document.id
        const status = dataItem.status || 'Pendiente'
        const statusDate = dataItem.statusDate ? new Date(dataItem.statusDate).toLocaleDateString('es-MX') : ''

        let displayName = ''
        let showStatusBelow = false

        if (isFreeLoad) {
            if (hasDocumentUploaded) {
                displayName = dataItem.document.name
                showStatusBelow = true
            } else {
                displayName = 'No se ha cargado ningún archivo'
                showStatusBelow = false
            }
        } else if (isRequiredDocument) {
            // Es un documento requerido
            if (hasDocumentUploaded) {
                // Ya se cargó un documento, mostrar el nombre requerido con el estatus debajo
                displayName = dataItem.name
                showStatusBelow = true
            } else {
                // No se ha cargado, mostrar el nombre requerido con asterisco
                displayName = `${dataItem.name} *`
                showStatusBelow = true
            }
        } else {
            displayName = 'No se ha cargado ningún archivo'
            showStatusBelow = false
        }

        return (
            <td className={`documentPanelBarDocumentRequested-cell-one ${dataItem.selected ? 'documentPanelBarVersions-cell-selected' : ''}`} onClick={() => handleSelectFileExp(dataItem)}>
                <div className='gridHLPure-userName'>
                    <div>{displayName}</div>
                </div>
                {showStatusBelow &&
                    <div className='documentPanelBarDocumentRequested-status'>
                        <span>{status}{(status !== 'Pendiente' && statusDate) ? ` el ${statusDate}` : ''}</span>
                    </div>}
            </td>
        )
    }

    const cellColumnTwo = (props) => {
        const { dataItem } = props
        const status = dataItem.status || 'Pendiente'

        if (!userIsApplicant) {
            const getTooltipText = (status) => {
                switch (status) {
                    case 'Pendiente':
                        return t('documentPanelBarDocumentRequested.tooltip.pending.description')
                    case 'Cargado':
                        return t('documentPanelBarDocumentRequested.tooltip.loaded.description')
                    case 'Aceptado':
                        return t('documentPanelBarDocumentRequested.tooltip.accepted.description')
                    case 'Rechazado':
                        return t('documentPanelBarDocumentRequested.tooltip.rejected.description')
                    default:
                        return ''
                }
            }

            const tooltipText = getTooltipText(status)
            const isRejected = status === 'Rechazado'

            const handleRejectionClick = () => {
                const reason = dataItem.comments || t('documentPanelBarDocumentRequested.rejection.default', { defaultValue: 'Sin motivo especificado' })
                setDocumentToReject(dataItem)
                setComments(reason)
                setIsViewingRejection(true)
                setShowRejectionModal(true)
            }

            return (
                <td className={`documentPanelBarDocumentRequested-cell-two ${dataItem.selected ? 'documentPanelBarVersions-cell-selected' : ''}`}>
                    <div className='documentPanelBarDocumentRequested-status' style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Tooltip
                            openDelay={600}
                            position='top'
                            anchorElement='target'
                            tooltipClassName='tooltipGeneralStyle'
                        >
                            <span title={tooltipText} style={{ cursor: 'pointer' }}>
                                {status}
                            </span>
                        </Tooltip>
                        {isRejected && (
                            <img
                                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}microservices/MiFirma/assets/digital-vault/icon_file_rejected.svg`}
                                alt={t('documentPanelBarDocumentRequested.rejection.icon.alt', { defaultValue: 'Motivo de rechazo' })}
                                style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                onClick={handleRejectionClick}
                            />
                        )}
                    </div>
                </td>
            )
        }

        return (
            <td className={`documentPanelBarDocumentRequested-cell-two ${dataItem.selected ? 'documentPanelBarVersions-cell-selected' : ''}`}>
                <div style={{ fontSize: '14px', color: '#2C353C' }}>
                </div>
            </td>
        )
    }

    const cellColumnThree = (props) => {
        const { dataItem } = props
        const status = dataItem.status || 'Pendiente'
        const config = getStatusConfig(status)

        return (
            <td className={`documentPanelBarDocumentRequested-cell-three ${dataItem.selected ? 'documentPanelBarVersions-cell-selected' : ''}`}>
                {!isInDeleteFlow && (
                    <div className='documentPanelBarDocumentRequested-general-grid-col-3'>
                        {/* Botón Ver (móvil) */}
                        {config.showVer && (
                            <div
                                className='cellActions-mobileExpanded'
                                onClick={() => handleSelectFileExp(dataItem)}
                            >
                                <img
                                    className='cellActions-icon-arrow'
                                    alt={'cellActions-arrow'}
                                    src={ArrowBtnMobile}
                                />
                            </div>
                        )}

                        {/* Botón Cargar */}
                        {!isCancelledRequest && config.showCargar && (
                            <>
                                {is530 ? (
                                    <img
                                        onClick={() => handleUploadDoc(dataItem)}
                                        src={ArrowBtnMobile}
                                        alt="upload"
                                        style={{ width: '18px', height: '18px' }}
                                    />
                                ) : (
                                    <button
                                        className='grid-btn-action'
                                        onClick={() => handleUploadDoc(dataItem)}
                                    >
                                        <p>{t('upload.btn')}</p>
                                    </button>
                                )}
                            </>
                        )}

                        {/* Botón Ver (desktop) */}
                        {config.showVer && (
                            <button className='grid-btn-action' onClick={() => handleSelectFileExp(dataItem)}>
                                <p>{t('documentPanelBarExpediente-watch-doc')}</p>
                            </button>
                        )}

                        {/* Botón Eliminar */}
                        {!isCancelledRequest && config.showEliminar && (
                            <ButtonTrashHLPure openDelay={600} onClickBtn={() => handleDeleteDoc(dataItem)} />
                        )}
                    </div>
                )}
            </td>
        )
    }

    // Usar los datos reales del servicio
    const dataToDisplay = filesExpediente

    // Preparar datos enriquecidos cuando userIsApplicant = true O userIsValidator = true O cuando el workflow está completado
    const enrichedUsersData = (userIsApplicant || userIsValidator || userIsShared || isCompletedWorkflow) ? filesExpediente.map(user => ({
        ...user,
        expanded: user.recipientId === expandedUserId
    })) : []

    const heightGrid = 'var(--panelHeight)'
    const total = dataToDisplay.length

    // Título dinámico según infoType
    const panelTitle = (userIsApplicant || userIsValidator || userIsShared || isCompletedWorkflow)
        ? (infoType === 'Expediente'
            ? t('documentPanelBarDocumentsRequested.title.expediente')
            : t('documentPanelBarDocumentsRequested.title.documento')
        )
        : t('documentPanelBarDocumentsRequested.title.default')

    useEffect(() => {
        if (isCancelledRequest) {
            setShowCanceledModal(true)
        }
    }, [isCancelledRequest])

    return (
        <div className='documentPanelBarDocumentRequested-container'>
            <PanelBarGLPure
                expandedPanel={true}
                titlePanel={panelTitle}
                className='documentPanelBarDocumentRequested'
                id='documentPanelBarDocumentRequested'
                actionComponent={
                    !isInDeleteFlow && !isCancelledRequest ? (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {shouldShowAddButton && (
                                <ButtonAddHLPure
                                    nameBtn={
                                        is1040
                                            ? is530
                                                ? ''
                                                : t('documentPanelBarDocumentsRequested.add.document')
                                            : t('documentPanelBarDocumentsRequested.add.document')
                                    }
                                    onClickBtn={handleAddDocument}
                                    iconAdd
                                />
                            )}
                        </div>
                    ) : null
                }
            >
                {userIsApplicant && !isCancelledRequest && !showSendButton && !allUsersCompleted && (
                    <div className='document-panel-bar-documentRequested-buttons-userIsApplicant'>
                        <ButtonAddHLPure
                            nameBtn={t('documentPanelBarDocumentsRequested.button.cancelRequest')}
                            onClickBtn={() => setShowCancelRequestModal(true)}
                        />
                        <ButtonAddHLPure
                            nameBtn={t('documentPanelBarDocumentsRequested.button.closeRequest')}
                            onClickBtn={handleCloseRequestClick}
                        />
                    </div>
                )}
                {userIsValidator && !isCancelledRequest && !showSendButton && (
                    <div className='document-panel-bar-documentRequested-buttons-userIsApplicant'>
                        <ButtonAddHLPure
                            nameBtn={t('documentPanelBarDocumentsRequested.button.rejectRequest')}
                            onClickBtn={() => setShowRejectRequestModal(true)}
                        />
                        <ButtonAddHLPure
                            nameBtn={t('documentPanelBarDocumentsRequested.button.approveRequest')}
                            onClickBtn={() => setShowApproveRequestModal(true)}
                        />
                    </div>
                )}
                {!userIsApplicant && !userIsValidator && instructions && instructions.trim() !== '' && (
                    <div className='documentPanelBarDocumentRequested-instructions-container'>
                        <div className='documentPanelBarDocumentRequested-instructions-icon'>
                            <img
                                src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_ICONS_BOVEDA_DIGITAL_TOOLBAR_INFO}`}
                                alt={'info'}
                            />
                            <span className='documentPanelBarDocumentRequested-instructions-label'>
                                {t('documentPanelBarDocumentRequested.instructions.label')}
                            </span>
                        </div>
                        <div className='documentPanelBarDocumentRequested-instructions-content'>
                            <span>{instructions}</span>
                        </div>
                    </div>
                )}
                <Grid item className='documentPanelBarDocumentRequested-grid-documents'>
                    {(userIsApplicant || userIsValidator || userIsShared || isCompletedWorkflow) ? (
                        <GridSelectionHLPure
                            styleGeneral={true}
                            styleGeneralClass={'documentPanelBarDocumentRequested-general-grid'}
                            pageable={false}
                            skip={0}
                            take={enrichedUsersData.length}
                            dataGrid={enrichedUsersData}
                            filter={false}
                            columnTwo={!is530}
                            titleRow1={t('documentPanelBarDocumentsRequested.user.column')}
                            titleRow2={!is530 && !showSendButton ? t('documentPanelBarDocumentsRequested.column.status') : undefined}
                            headerColumnOne={headerColumnUserName}
                            headerColumnTwo={!is530 ? headerColumnUserStatus : undefined}
                            cellColumnOne={cellColumnUserName}
                            cellColumnTwo={!is530 ? cellColumnUserStatus : undefined}
                            columnThree={true}
                            titleRow3={!isCompletedWorkflow ? t('documentPanelBarDocumentsRequested-action') : ''} headerColumnThree={headerColumnUserEmail}
                            cellColumnThree={cellColumnUserEmail}
                            styleGrid={{ height: heightGrid }}
                            detail={UserDocumentsDetailRow}
                            expandField='expanded'
                            onExpandChange={handleExpandChange}
                        />
                    ) : (
                        <GridSelectionHLPure
                            styleGeneral={true}
                            styleGeneralClass={'documentPanelBarDocumentRequested-general-grid'}
                            pageable={false}
                            skip={0}
                            take={total}
                            dataGrid={dataToDisplay}
                            filter={false}
                            columnTwo={true}
                            titleRow1={t('documentPanelBarDocumentsRequested-documents')}
                            titleRow2={!is530 ? t('documentPanelBarDocumentsRequested.column.status') : undefined}
                            headerColumnOne={headerColumnOne}
                            headerColumnTwo={!is530 ? headerColumnTwo : undefined}
                            cellColumnOne={cellColumnOne}
                            cellColumnTwo={!is530 ? cellColumnTwo : undefined}
                            columnThree={true}
                            titleRow3={!isCompletedWorkflow ? t('documentPanelBarDocumentsRequested-action') : ''} headerColumnThree={headerColumnThree}
                            cellColumnThree={cellColumnThree}
                            styleGrid={{ height: heightGrid }}
                        />
                    )}
                </Grid>
            </PanelBarGLPure>


            {/* Modal de motivo de rechazo */}
            {showRejectionModal && (
                <InteractiveDialog
                    title={t('documentPanelBarDocumentRequested.modal.rejectionReason.title', { defaultValue: 'Motivo de rechazo' })}
                    size='M'
                    onClose={() => {
                        setShowRejectionModal(false)
                        setComments('')
                        setDocumentToReject(null)
                        setIsViewingRejection(false)
                    }}
                    cancelBtnTitle={t('button.close', { defaultValue: 'Cerrar' })}
                    saveBtnTitle={isViewingRejection ? null : t('button.save', { defaultValue: 'Guardar' })}
                    onSave={isViewingRejection ? null : handleSubmitRejection}
                    disabledSubmitBtn={isViewingRejection ? false : !comments.trim()}
                >
                    <div>
                        <TextAreaHLPure
                            id='rejection-reason'
                            name='rejectionReason'
                            labelTextArea={isViewingRejection
                                ? t('documentPanelBarDocumentRequested.modal.rejectionReason.labelView') : t('documentPanelBarDocumentRequested.modal.rejectionReason.label')
                            }
                            valueTextData={comments}
                            handleChangeTextArea={(e) => {
                                if (!isViewingRejection) {
                                    const value = e.target.value
                                    if (value.length <= 500) {
                                        setComments(value)
                                    }
                                }
                            }}
                            rowsTextArea={4}
                            characterLimit={500}
                            disabled={isViewingRejection}
                        />
                    </div>
                </InteractiveDialog>
            )}

            {/* Modal de límite de documentos alcanzado */}
            {showLimitModal && (
                <InteractiveDialog
                    title={t('documentPanelBarDocumentRequested.limit.modal.title', { defaultValue: 'Límite de documentos' })}
                    onClose={() => setShowLimitModal(false)}
                    cancelBtnTitle={t('button.close')}
                >
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <img
                            src={`${process.env.REACT_APP_AWS_S3_BUCKET_URL}microservices/MiFirma/assets/digital-vault/icon_warning.svg`}
                            alt="Warning"
                            style={{ width: '60px', height: '60px', marginBottom: '20px' }}
                        />
                        <p>
                            {t('documentPanelBarDocumentRequested.limit.modal.message', {
                                defaultValue: `Has alcanzado el límite de carga de documentos definido por {{email}}. Puedes sustituir un archivo por otro o contactarle para solicitar una ampliación del límite.`,
                                email: 'mvargas@govo.digital'
                            })}
                        </p>
                    </div>
                </InteractiveDialog>
            )}
            {/* Modal para ver detalles de la solicitud */}
            {showDetailsModal && (
                <RequestDetailsDialog
                    showDialog={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false)
                        setSelectedRequestForDetails(null)
                    }}
                    requestData={selectedRequestForDetails}
                />
            )}

            {/* Modal de confirmación para Cancelar solicitud */}
            <DialogSystemGLPure
                showConfirm={showCancelRequestModal}
                confirmTitle={t('documentPanelBarDocumentsRequested.modal.cancelRequest.title')}
                confirmMessage={t('documentPanelBarDocumentsRequested.modal.cancelRequest.message')}
                handleConfirmDeleteFlowClose={() => setShowCancelRequestModal(false)}
                handleConfirmDeleteFlowSubmit={handleCancelRequestLocal}
                labelCancelConfirmation={t('button.cancel')}
                labelSubmitConfirmation={t('button.accept')}
            />

            {/* Modal de confirmación para Cerrar solicitud */}
            <DialogSystemGLPure
                showConfirm={showCloseRequestModal}
                confirmTitle={t('documentPanelBarDocumentsRequested.modal.closeRequest.title')}
                confirmMessage={t('documentPanelBarDocumentsRequested.modal.closeRequest.message')}
                handleConfirmDeleteFlowClose={() => setShowCloseRequestModal(false)}
                handleConfirmDeleteFlowSubmit={handleCloseRequestLocal}
                labelCancelConfirmation={t('button.cancel')}
                labelSubmitConfirmation={t('button.accept')}
            />

            <DialogSystemGLPure
                isWarnign={showNoFilesWarning}
                titleDialog={t('documentPanelBarDocumentsRequested.modal.noFiles.title')}
                messageWarning={t('documentPanelBarDocumentsRequested.modal.noFiles.message')}
                handleWarningClose={() => setShowNoFilesWarning(false)}
                showBtnSubmitWarning={true}
                labelWarningBtnSubmit={t('button.close')}
                handleWarningSubmit={() => setShowNoFilesWarning(false)}
            />

            {/* Modal de confirmación para Marcar no requerido */}
            <DialogSystemGLPure
                showConfirm={showMarkNotRequiredModal}
                confirmTitle={t('documentPanelBarDocumentsRequested.modal.markNotRequired.title')}
                confirmMessage={t('documentPanelBarDocumentsRequested.modal.markNotRequired.message')}
                handleConfirmDeleteFlowClose={() => {
                    setShowMarkNotRequiredModal(false)
                    setDocumentToMarkNotRequired(null)
                }}
                handleConfirmDeleteFlowSubmit={handleMarkNotRequired}
                labelCancelConfirmation={t('button.cancel')}
                labelSubmitConfirmation={t('button.accept')}
            />

            {/* Modal de confirmación para Aprobar solicitud (Validador) */}
            <DialogSystemGLPure
                showConfirm={showApproveRequestModal}
                confirmTitle={t('documentPanelBarDocumentsRequested.modal.approveRequest.title')}
                confirmMessage={t('documentPanelBarDocumentsRequested.modal.approveRequest.message')}
                handleConfirmDeleteFlowClose={() => setShowApproveRequestModal(false)}
                handleConfirmDeleteFlowSubmit={handleApproveRequest}
                labelCancelConfirmation={t('button.cancel')}
                labelSubmitConfirmation={t('button.accept')}
            />

            {/* Modal de confirmación para Rechazar solicitud (Validador) */}
            <DialogSystemGLPure
                showConfirm={showRejectRequestModal}
                confirmTitle={t('documentPanelBarDocumentsRequested.modal.rejectRequest.title')}
                confirmMessage={t('documentPanelBarDocumentsRequested.modal.rejectRequest.message')}
                handleConfirmDeleteFlowClose={() => setShowRejectRequestModal(false)}
                handleConfirmDeleteFlowSubmit={handleRejectRequest}
                labelCancelConfirmation={t('button.cancel')}
                labelSubmitConfirmation={t('button.accept')}
            />

            {/* Modal de cancelación de solicitud */}
            <DialogSystemGLPure
                isWarnign={showCanceledModal}
                titleDialog={t('documentPanelBarDocumentsRequested.modal.canceled.title')}
                messageWarning={t('documentPanelBarDocumentsRequested.modal.canceled.message')}
                handleWarningClose={() => setShowCanceledModal(false)}
                showBtnSubmitWarning={true}
                labelWarningBtnSubmit={t('button.close')}
                handleWarningSubmit={() => setShowCanceledModal(false)}
            />

            {showRejectRequestReasonModal && (
                <InteractiveDialog
                    title={t('documentPanelBarDocumentsRequested.modal.rejectRequestReason.title', {
                        defaultValue: 'Motivo de rechazo'
                    })}
                    size='M'
                    onClose={() => {
                        setShowRejectRequestReasonModal(false)
                        setRejectRequestComments('')
                    }}
                    cancelBtnTitle={t('button.cancel')}
                    saveBtnTitle={t('button.save')}
                    onSave={handleSubmitRejectRequest}
                    disabledSubmitBtn={!rejectRequestComments.trim()}
                >
                    <div style={{ padding: '20px 0' }}>
                        <TextAreaHLPure
                            id='reject-request-reason'
                            name='rejectRequestReason'
                            labelTextArea={t('documentPanelBarDocumentsRequested.modal.rejectRequestReason.label', {
                                defaultValue: 'Escribe el motivo de rechazo *'
                            })}
                            valueTextData={rejectRequestComments}
                            handleChangeTextArea={(e) => {
                                const value = e.target.value
                                if (value.length <= 500) {
                                    setRejectRequestComments(value)
                                }
                            }}
                            rowsTextArea={4}
                            characterLimit={500}
                        />
                    </div>
                </InteractiveDialog>
            )}

        </div>
    )
}

export default DocumentPanelBarDocumentsRequested